import { useQuery } from '@tanstack/react-query';
import { pbAuthedFetch } from "../authHeaders";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const CHART_COLORS = [
  'var(--gold)',
  'var(--ok)',
  'var(--risk)',
  'var(--ink-45)',
  'var(--amber)',
  'var(--gd2)',
  'var(--state-unowned, #A8322D)',
  'var(--green, #166F55)',
];

const SERVICE_GRADIENTS: Record<string, { from: string; to: string }> = {
  EC2: { from: '#FF9900', to: '#FFB84D' },
  RDS: { from: '#3366CC', to: '#5C8AE6' },
  S3: { from: '#166F55', to: '#2E7D5B' },
  Lambda: { from: '#8B5CF6', to: '#A78BFA' },
  ELB: { from: '#0C5A92', to: '#1476BE' },
  CloudFront: { from: '#B32D1E', to: '#D94A3D' },
  'IAM Users': { from: '#895809', to: '#B87A12' },
  'IAM Roles': { from: '#8A7340', to: '#A89060' },
};

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
}

export function AwsDashboardPage() {
  const { data: inventory, isLoading: invLoading, error: invError } = useQuery<any>({
    queryKey: ['aws-inventory'],
    queryFn: () => pbAuthedFetch('/api/aws/inventory').then(r => { if (!r.ok) throw new Error(`Request failed: ${r.status}`); return r.json(); }),
    refetchInterval: 60_000,
  });

  const { data: costs, isLoading: costLoading, error: costError } = useQuery<any>({
    queryKey: ['aws-costs'],
    queryFn: () => pbAuthedFetch('/api/aws/costs').then(r => { if (!r.ok) throw new Error(`Request failed: ${r.status}`); return r.json(); }),
    refetchInterval: 60_000,
  });

  const { data: alerts } = useQuery<any>({
    queryKey: ['aws-alerts'],
    queryFn: () => pbAuthedFetch('/api/aws/alerts').then(r => { if (!r.ok) throw new Error(`Request failed: ${r.status}`); return r.json(); }),
    refetchInterval: 60_000,
  });

  const isLoading = invLoading || costLoading;
  const hasError = invError || costError;

  /* ── Shared pagehead ──────────────────────────────────────────── */
  const pagehead = (
    <div className="pagehead">
      <div className="pagehead__row">
        <div className="pagehead__text">
          <p className="pagehead__eyebrow">Cloud</p>
          <h1 className="pagehead__title">AWS Dashboard</h1>
          <div className="pagehead__facts">
            <span className="fact">Infrastructure inventory &amp; cost monitoring</span>
            <span className="fact">Auto-refreshes every 60 s</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <>
        {pagehead}
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading AWS data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (hasError) {
    return (
      <>
        {pagehead}
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load AWS data</h3>
                <p className="state__body">
                  Could not connect to the AWS API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => window.location.reload()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const isMock = inventory?.mock || costs?.mock;

  /* ── Cost calculations ─────────────────────────────────────────── */
  const dailyCosts: { date: string; cost: number }[] = costs?.dailyCosts ?? [];
  const totalCost = dailyCosts.reduce((sum, d) => sum + d.cost, 0);
  const daysElapsed = dailyCosts.length || 1;
  const dailyAvg = totalCost / daysElapsed;
  const projected = dailyAvg * 30;

  const prevMonthCost = costs?.previousMonthTotal ?? totalCost;
  const costChange = prevMonthCost > 0 ? ((totalCost - prevMonthCost) / prevMonthCost) * 100 : 0;
  const costUp = costChange >= 0;

  const serviceCosts: { name: string; value: number }[] = costs?.serviceCosts ?? [];

  /* ── Inventory data ────────────────────────────────────────────── */
  const inv = inventory?.services ?? {};

  const inventoryCards = [
    {
      name: 'EC2',
      count: inv.ec2?.total ?? 0,
      details: `${inv.ec2?.running ?? 0} running, ${inv.ec2?.stopped ?? 0} stopped`,
    },
    {
      name: 'RDS',
      count: inv.rds?.total ?? 0,
      details: `${inv.rds?.available ?? 0} available, ${inv.rds?.stopped ?? 0} stopped`,
    },
    {
      name: 'S3',
      count: inv.s3?.total ?? 0,
      details: inv.s3?.totalSizeGB != null ? `${inv.s3.totalSizeGB.toFixed(1)} GB total` : '—',
    },
    {
      name: 'Lambda',
      count: inv.lambda?.total ?? 0,
      details: `${inv.lambda?.active ?? 0} active, ${inv.lambda?.inactive ?? 0} inactive`,
    },
    {
      name: 'ELB',
      count: inv.elb?.total ?? 0,
      details: `${inv.elb?.active ?? 0} active, ${inv.elb?.inactive ?? 0} inactive`,
    },
    {
      name: 'CloudFront',
      count: inv.cloudfront?.total ?? 0,
      details: `${inv.cloudfront?.enabled ?? 0} enabled, ${inv.cloudfront?.disabled ?? 0} disabled`,
    },
    {
      name: 'IAM Users',
      count: inv.iam?.users ?? 0,
      details: `${inv.iam?.activeUsers ?? 0} active`,
    },
    {
      name: 'IAM Roles',
      count: inv.iam?.roles ?? 0,
      details: `${inv.iam?.activeRoles ?? 0} active`,
    },
  ];

  const instanceTypes: { type: string; count: number }[] = inv.ec2?.instanceTypes ?? [];
  const alertList: { level: string; message: string }[] = alerts?.alerts ?? [];

  return (
    <>
      {pagehead}

      <div className="pagebody">
        {/* ── Mock data notice ─────────────────────────────────────── */}
        {isMock && (
          <div className="notice notice--warn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <div>
              <p className="notice__title">Running on mock data</p>
              <p className="notice__body">Connect the AWS API for live figures.</p>
            </div>
          </div>
        )}

        {/* ── Alert notices ────────────────────────────────────────── */}
        {alertList.map((a, i) => (
          <div key={i} className={`notice ${a.level === 'critical' ? 'notice--risk' : a.level === 'warning' ? 'notice--warn' : 'notice--info'}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              {a.level === 'critical' ? (
                <>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </>
              ) : a.level === 'warning' ? (
                <>
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </>
              ) : (
                <>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </>
              )}
            </svg>
            <div>
              <p className="notice__body">{a.message}</p>
            </div>
          </div>
        ))}

        {/* ── Cost summary indicators ──────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Cost summary">
          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">This Month</p>
              <p className="ind__full">Total AWS spend so far</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{formatCurrency(totalCost)}</p>
              <span className={`ind__delta ${costUp ? 'ind__delta--bad' : 'ind__delta--good'}`}>
                {costUp ? '▲' : '▼'} {Math.abs(costChange).toFixed(1)}%
              </span>
            </div>
            <p className="ind__reading">
              <i></i> vs {formatCurrency(prevMonthCost)} last month
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Daily Avg</p>
              <p className="ind__full">Average daily spend</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{formatCurrency(dailyAvg)}</p>
            </div>
            <p className="ind__reading">
              <i></i> Over <b>{daysElapsed}</b> day{daysElapsed !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Projected</p>
              <p className="ind__full">30-day cost estimate</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{formatCurrency(projected)}</p>
            </div>
            <p className="ind__reading">
              <i></i> Based on current daily average
            </p>
          </div>
        </div>

        {/* ── Cost breakdown charts ────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-4)' }}>
          {/* Daily cost bar chart */}
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Cost</p>
                <h2 className="box__title">Daily Cost</h2>
              </div>
            </div>
            <div className="box__body">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={dailyCosts} margin={{ top: 4, right: 8, bottom: 4, left: -8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--hair)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: 'var(--ink-45)' }}
                    tickFormatter={(v: string) => v.slice(5)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--ink-45)' }}
                    tickFormatter={(v: number) => `$${v}`}
                  />
                  <Tooltip
                    formatter={(value: any) => [formatCurrency(Number(value)), 'Cost']}
                    contentStyle={{ fontSize: 'var(--text-sm)', borderRadius: 'var(--r-ctl)', border: '1px solid var(--hair)' }}
                  />
                  <Bar dataKey="cost" fill="var(--gold)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Service cost pie chart */}
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Cost</p>
                <h2 className="box__title">Cost by Service</h2>
              </div>
            </div>
            <div className="box__body">
              {serviceCosts.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={serviceCosts}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                      style={{ fontSize: 11 }}
                    >
                      {serviceCosts.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: any) => [formatCurrency(Number(value)), 'Cost']}
                      contentStyle={{ fontSize: 'var(--text-sm)', borderRadius: 'var(--r-ctl)', border: '1px solid var(--hair)' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="state">
                  <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <h3 className="state__title">No service cost data</h3>
                  <p className="state__body">Connect the AWS API to see cost breakdown by service.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Inventory grid ───────────────────────────────────────── */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Inventory</p>
              <h2 className="box__title">AWS Services</h2>
            </div>
          </div>
          <div className="box__body">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 'var(--sp-4)',
              }}
            >
              {inventoryCards.map((svc) => {
                const grad = SERVICE_GRADIENTS[svc.name] ?? { from: 'var(--gold)', to: 'var(--gd2)' };
                return (
                  <div
                    key={svc.name}
                    style={{
                      background: 'var(--surface-sub)',
                      border: '1px solid var(--hair)',
                      borderRadius: 'var(--r-box)',
                      padding: 'var(--sp-4)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--sp-2)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 'var(--r-ctl)',
                          background: `linear-gradient(135deg, ${grad.from}, ${grad.to})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 'var(--text-xs)',
                          letterSpacing: '0.04em',
                          fontFamily: 'var(--mono)',
                        }}
                      >
                        {svc.name.slice(0, 2).toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: 'var(--text-base)', color: 'var(--ink)' }}>{svc.name}</span>
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--text-2xl)',
                        fontWeight: 700,
                        fontFamily: 'var(--serif)',
                        fontStyle: 'italic',
                        color: 'var(--ink)',
                        lineHeight: 1,
                      }}
                    >
                      {svc.count.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)', fontFamily: 'var(--mono)' }}>{svc.details}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── EC2 Instance Types ───────────────────────────────────── */}
        {instanceTypes.length > 0 && (
          <div className="box">
            <div className="box__head">
              <div className="box__head-text">
                <p className="box__eyebrow">Compute</p>
                <h2 className="box__title">EC2 Instance Types</h2>
              </div>
            </div>
            <div className="box__body box__body--flush">
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th><button className="th">Instance Type</button></th>
                      <th><button className="th th--num">Count</button></th>
                      <th style={{ width: '40%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {instanceTypes
                      .sort((a, b) => b.count - a.count)
                      .map((it) => {
                        const maxCount = instanceTypes[0]?.count ?? 1;
                        const pct = (it.count / maxCount) * 100;
                        return (
                          <tr key={it.type}>
                            <td className="td--id">{it.type}</td>
                            <td className="td--num">{it.count}</td>
                            <td>
                              <div
                                style={{
                                  height: 8,
                                  borderRadius: 'var(--r-xs)',
                                  background: 'var(--surface-sub)',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    width: `${pct}%`,
                                    height: '100%',
                                    borderRadius: 'var(--r-xs)',
                                    background: 'var(--gold)',
                                    transition: 'width 0.3s ease',
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
